interface BreadcrumbDocument extends Document {
    _adminBreadcrumbInited?: boolean;
}

function dropdowns(root: ParentNode = document): HTMLDetailsElement[] {
    return Array.from(root.querySelectorAll<HTMLDetailsElement>("details[data-admin-breadcrumb-dropdown]"));
}

function menus(root: ParentNode = document): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>("[data-admin-breadcrumb-menu]"));
}

function dropdownFor(menu: HTMLElement): HTMLDetailsElement | null {
    return menu.closest<HTMLDetailsElement>("details[data-admin-breadcrumb-dropdown]");
}

function toggleFor(menu: HTMLElement): HTMLElement | null {
    const id = menu.id;
    if (id === "") return null;
    return document.querySelector<HTMLElement>(`[data-admin-breadcrumb-toggle][aria-controls="${id}"]`);
}

function syncDropdown(dropdown: HTMLDetailsElement): void {
    const toggle = dropdown.querySelector<HTMLElement>("[data-admin-breadcrumb-toggle]");
    toggle?.setAttribute("aria-expanded", dropdown.open ? "true" : "false");
}

function closeDropdown(dropdown: HTMLDetailsElement): void {
    dropdown.open = false;
    syncDropdown(dropdown);
}

function closeMenu(menu: HTMLElement): void {
    const dropdown = dropdownFor(menu);
    if (dropdown) {
        closeDropdown(dropdown);
        return;
    }
    menu.hidden = true;
    toggleFor(menu)?.setAttribute("aria-expanded", "false");
}

function closeAll(except?: HTMLDetailsElement): void {
    dropdowns().forEach((dropdown) => {
        if (except && dropdown === except) return;
        closeDropdown(dropdown);
    });
    menus().forEach((menu) => {
        if (dropdownFor(menu)) return;
        closeMenu(menu);
    });
}

function focusItem(menu: HTMLElement, direction: 1 | -1 | 0): void {
    const items = Array.from(menu.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]'));
    if (items.length === 0) return;
    const activeIndex = items.findIndex((item) => item === document.activeElement);
    let nextIndex = direction === 0 ? 0 : activeIndex + direction;
    if (nextIndex < 0) nextIndex = items.length - 1;
    if (nextIndex >= items.length) nextIndex = 0;
    items[nextIndex]?.focus({ preventScroll: true });
}

export function initAdminBreadcrumb(root: BreadcrumbDocument = document): void {
    if (root._adminBreadcrumbInited) return;
    root._adminBreadcrumbInited = true;

    dropdowns(root).forEach((dropdown) => {
        syncDropdown(dropdown);
        dropdown.addEventListener("toggle", () => {
            if (dropdown.open) closeAll(dropdown);
            syncDropdown(dropdown);
        });
    });

    root.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const current = target.closest<HTMLElement>("[data-admin-breadcrumb-current]");
        if (current) {
            event.preventDefault();
            closeAll();
            return;
        }

        const toggle = target.closest<HTMLElement>("[data-admin-breadcrumb-toggle]");
        if (toggle) {
            const dropdown = toggle.closest<HTMLDetailsElement>("details[data-admin-breadcrumb-dropdown]");
            if (!dropdown) return;
            event.preventDefault();
            const shouldOpen = !dropdown.open;
            closeAll(shouldOpen ? dropdown : undefined);
            dropdown.open = shouldOpen;
            syncDropdown(dropdown);
            return;
        }

        const item = target.closest<HTMLElement>("[data-admin-breadcrumb-item-toggle]");
        if (!item || target.closest("[data-admin-breadcrumb-menu]")) return;
        const dropdown = item.querySelector<HTMLDetailsElement>("details[data-admin-breadcrumb-dropdown]");
        if (!dropdown) return;

        event.preventDefault();
        const shouldOpen = !dropdown.open;
        closeAll(shouldOpen ? dropdown : undefined);
        dropdown.open = shouldOpen;
        syncDropdown(dropdown);
    });

    document.addEventListener("click", (event) => {
        const target = event.target;
        if (target instanceof HTMLElement && target.closest("[data-admin-breadcrumb]")) {
            return;
        }
        closeAll();
    });

    root.addEventListener("keydown", (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const open = target.closest<HTMLElement>("[data-admin-breadcrumb-menu]:not([hidden])");
        if (event.key === "Escape") {
            const menu = open ?? menus().find((item) => {
                const dropdown = dropdownFor(item);
                return dropdown ? dropdown.open : !item.hidden;
            });
            if (!menu) return;
            event.preventDefault();
            closeMenu(menu);
            toggleFor(menu)?.focus({ preventScroll: true });
            return;
        }

        if (!open) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            focusItem(open, 1);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            focusItem(open, -1);
        } else if (event.key === "Home") {
            event.preventDefault();
            focusItem(open, 0);
        } else if (event.key === "End") {
            event.preventDefault();
            focusItem(open, -1);
        }
    });
}
